import dynamic from 'next/dynamic';

import { useAdDetailsContext } from 'contexts/AdDetailsContext';

import { Button } from 'components/Toolkit/Button/Button';
import {
  RevealPhoneNumber,
  type RevealPhoneNumberProps,
} from 'components/PageWrappers/SharedComponents/RevealPhoneNumber/RevealPhoneNumber';
import { Columns } from 'components/Toolkit/Columns/Columns';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { EmailDealerOrSeller } from '../EmailDealerOrSeller/EmailDealerOrSeller';
import { DisplayContainer } from 'components/Support/DisplayContainer/DisplayContainer';
import { ReserveCTA } from '../ReserveCTA/ReserveCTA';

import { type EmailDealerOrSellerProps } from '../EmailDealerOrSeller/EmailDealerOrSeller.typed';
import {
  DISPLAY_RESERVATION,
  FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE,
} from '../../../../../utils';

import * as Styled from './UserActionsStickyFooter.styled';

const ContactDealershipBottomSheet = dynamic(
  import('./subcomponents/ContactDealershipBottomSheet').then(
    (mod) => mod.ContactDealershipBottomSheet,
  ),
);

import { type ReservationStatuses } from '../../types';

type UserActionsStickyFooterProps = Omit<
  {
    phoneResponse: boolean;
    sectionName?: string;
    emailResponse: boolean;
    whatsappResponse: boolean;
    isViewingOwnAd?: boolean;
    editAdHref: string;
    handleBoost: () => Promise<void>;
    active: boolean;
    reservationStatus?: ReservationStatuses;
  } & EmailDealerOrSellerProps &
    RevealPhoneNumberProps,
  'dealerID' | 'clickSource'
>;

const UserActionsStickyFooter = (props: UserActionsStickyFooterProps) => {
  const {
    adId,
    emailResponse,
    phoneResponse,
    campaignId,
    sectionName,
    sellerType,
    whatsappResponse,
    handleReveal,
    isViewingOwnAd,
    editAdHref,
    handleBoost,
    active,
    reservationStatus,
  } = props;

  const CLICK_SOURCE = 'mobile-sticky';

  const { setIsContactSellerModalOpen } = useAdDetailsContext();

  const displayReservations = reservationStatus && DISPLAY_RESERVATION;

  const ctas = [emailResponse, phoneResponse, displayReservations];
  const ctaCount = ctas.reduce((total: number, option: boolean) => {
    return total + (option ? 1 : 0);
  }, 0);
  /**
   * Up to two CTAs can be visible in the footer at any time.
   *
   * If reservations are available, this option should always be displayed, along with the contact option.
   *
   * If more than one contact option is availble, we put all contact options behind the contact dealership CTA.
   */
  const maximumNumberOfVisibleCtasExceeded = ctaCount > 2;

  /**
   * WhatsApp should only be available from the contact dealership option, along with any other contact options.
   */
  const displayContactDealershipBottomSheet =
    maximumNumberOfVisibleCtasExceeded || whatsappResponse;

  const PhoneContactProps: RevealPhoneNumberProps = {
    adId,
    clickSource: CLICK_SOURCE,
    isPrimaryButton: !emailResponse,
    campaignId,
    showNumberTextVariant: true,
    sectionName,
    sellerType,
  };

  const EmailSellerProps: EmailDealerOrSellerProps = {
    adId,
    clickSource: CLICK_SOURCE,
    dealerID: 'dealer-enquiry-btn-footer',
    handleReveal,
    sellerType,
  };

  return (
    <>
      <Styled.MobileAndTabletStickyFooter
        position="fixed"
        active={FEATURE_ADP_STICKY_CTA_ALWAYS_VISIBLE ? true : active}
        data-testid="mobile-sticky-cta"
      >
        {isViewingOwnAd ? (
          <Columns>
            <Button onClick={handleBoost} data-testid="boost-button">
              Boost
            </Button>
            <ButtonLink href={editAdHref} ofType="SECONDARY">
              Edit
            </ButtonLink>
          </Columns>
        ) : (
          <>
            {/**
             * WhatsApp should only be visible on mobile and behind the contact dealership button.
             *
             * If WhatsApp is not available or the screen size is not small,
             * we omit WhatsApp as an option and therefore the contact dealership button. */}
            <DisplayContainer
              config={{ small: 'block', medium: 'none', large: 'none' }}
            >
              <Columns>
                {displayReservations && (
                  <ReserveCTA reservationStatus={reservationStatus} />
                )}
                {displayContactDealershipBottomSheet && (
                  <Button onClick={() => setIsContactSellerModalOpen(true)}>
                    Contact Dealership
                  </Button>
                )}
                {!displayContactDealershipBottomSheet && phoneResponse && (
                  <RevealPhoneNumber {...PhoneContactProps} />
                )}
                {!displayContactDealershipBottomSheet && emailResponse && (
                  <EmailDealerOrSeller {...EmailSellerProps} />
                )}
              </Columns>
            </DisplayContainer>
            <DisplayContainer
              config={{ small: 'none', medium: 'block', large: 'none' }}
            >
              <Columns>
                {displayReservations && (
                  <ReserveCTA reservationStatus={reservationStatus} />
                )}
                {maximumNumberOfVisibleCtasExceeded && (
                  <Button onClick={() => setIsContactSellerModalOpen(true)}>
                    Contact Dealership
                  </Button>
                )}
                {!maximumNumberOfVisibleCtasExceeded && phoneResponse && (
                  <RevealPhoneNumber {...PhoneContactProps} />
                )}
                {!maximumNumberOfVisibleCtasExceeded && emailResponse && (
                  <EmailDealerOrSeller {...EmailSellerProps} />
                )}
              </Columns>
            </DisplayContainer>
          </>
        )}
      </Styled.MobileAndTabletStickyFooter>
      {displayContactDealershipBottomSheet && (
        <ContactDealershipBottomSheet
          adId={adId}
          emailResponse={emailResponse}
          phoneResponse={phoneResponse}
          sectionName={sectionName}
          campaignId={campaignId}
          handleReveal={handleReveal}
          whatsappResponse={whatsappResponse}
        />
      )}
    </>
  );
};

export { UserActionsStickyFooter, type UserActionsStickyFooterProps };
